import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import Parse from "parse";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { Stage } from "../../shared/CaseConfiguration";
import { LedgerItems } from "../../shared/ledger/items";
import { Graph } from "./Graph";
import { graphColors } from "./graphColors";
import {
  LedgerLook,
  OutcomeLedgers,
  ledgerItemsToDot,
  outcomeFlowDot,
} from "./ledgerDot";

/** Identifies one outcome's ledger for the on-demand DAG recompute. */
export type LedgerDagRef = {
  caseId: string;
  partyId: string;
  outcomeIndex: number;
};

/**
 * `items`: the analysed party's ledger as a statement (its costs, the facts
 * and payments beside them, what causes what). `flow`: the actor-level money
 * flow across ALL parties of the outcome (who pays whom what, net per actor).
 */
export type LedgerGraphView = "items" | "flow";

type Payload =
  | { view: "items"; items: LedgerItems }
  | { view: "flow"; outcome: OutcomeLedgers };

/**
 * Dev tool: fetches the provenance items behind one outcome (recomputed
 * server-side on demand — never persisted) and renders the selected graph
 * view with graphviz. The call site offers it to the users the `decisionTree`
 * feature toggle is on for.
 */
export const LedgerDagDialog = ({
  dag,
  view,
  open,
  onClose,
}: {
  dag: LedgerDagRef;
  view: LedgerGraphView;
  open: boolean;
  onClose: () => void;
}) => {
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const parties = useCase().getPartyDescriptors();
  const [payload, setPayload] = useState<Payload>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setPayload(undefined);
    setError(undefined);
    const fetched: Promise<Payload> =
      view === "flow"
        ? Parse.Cloud.run("ledgersOfOutcome", {
            caseId: dag.caseId,
            outcomeIndex: dag.outcomeIndex,
          }).then((outcome: OutcomeLedgers) => ({ view, outcome }))
        : Parse.Cloud.run("ledgerItemsOf", dag).then(
            (items: LedgerItems) => ({ view, items }),
          );
    fetched.then(setPayload).catch((e) => setError(String(e)));
  }, [open, view, dag.caseId, dag.partyId, dag.outcomeIndex]);

  const look: LedgerLook = {
    colors: graphColors(theme),
    money: currency.format,
    stageName: (stage) => t(`stages.${Stage[stage]}`),
    costTypeName: (type) => t(`costs.costTypes.${type}`),
  };
  const dot =
    payload === undefined
      ? undefined
      : payload.view === "flow"
        ? outcomeFlowDot(payload.outcome, look)
        : ledgerItemsToDot(
            payload.items,
            parties[dag.partyId]?.name ?? dag.partyId,
            look,
          );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ "& .MuiDialog-paper": { maxWidth: "95vw" } }}
    >
      <DialogTitle>
        {view === "flow"
          ? "Money flow (who pays whom)"
          : "Ledger (costs, payments and what causes them)"}
      </DialogTitle>
      <DialogContent sx={{ overflow: "auto" }}>
        {error !== undefined ? (
          <Typography color="error">{error}</Typography>
        ) : dot !== undefined ? (
          <Graph dot={dot} />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
